import React, { useEffect, useState, useCallback } from "react";
import { Check, X, Pencil, Trash2, Link2, UserPlus, Copy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import StatusBadge from "@/components/StatusBadge";
import { EmptyState, SectionHeader, fmtDate } from "@/components/PageHeader";

function StudentPicker({ roster, selected, onToggle, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-student-picker`}>
      {roster.length === 0 ? (
        <span className="text-xs text-slate-400">No approved team members to link yet.</span>
      ) : roster.map((m) => {
        const on = selected.includes(m.id);
        return (
          <button type="button" key={m.id} onClick={() => onToggle(m.id)} data-testid={`${testPrefix}-student-${m.id}`}
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {m.last_name}, {m.first_name}
          </button>
        );
      })}
    </div>
  );
}

export default function CoachParents() {
  const [requests, setRequests] = useState([]);
  const [parents, setParents] = useState([]);
  const [roster, setRoster] = useState([]);
  const [approveFor, setApproveFor] = useState(null); // request being approved
  const [approveSel, setApproveSel] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", is_active: true, linked_student_ids: [] });
  const [del, setDel] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: "", last_name: "", email: "", student_ids: [] });
  const [inviting, setInviting] = useState(false);
  const [setupLink, setSetupLink] = useState(null);

  const load = useCallback(async () => {
    const [r, p, m] = await Promise.all([
      api.get("/coach/parent-requests"),
      api.get("/coach/parents"),
      api.get("/coach/team-members"),
    ]);
    setRequests(r.data.items || []);
    setParents(p.data.items || []);
    setRoster(m.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openApprove = (req) => {
    setApproveFor(req);
    setApproveSel((req.suggested_students || []).map((s) => s.id));
  };
  const doApprove = async () => {
    if (approveSel.length === 0) { toast.error("Select at least one student to link"); return; }
    try {
      await api.post(`/coach/parent-requests/${approveFor.id}/approve`, { student_ids: approveSel });
      toast.success("Parent approved & linked");
      setApproveFor(null); load();
      window.dispatchEvent(new Event("rpg:badges-refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doReject = async () => {
    try {
      await api.post(`/coach/parent-requests/${rejectFor.id}/reject`);
      toast.success("Parent request rejected");
      setRejectFor(null); load();
      window.dispatchEvent(new Event("rpg:badges-refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ first_name: p.first_name, last_name: p.last_name, email: p.email,
      is_active: p.is_active !== false, linked_student_ids: (p.linked_students || []).map((s) => s.id) });
  };
  const doEdit = async () => {
    try {
      await api.put(`/coach/parents/${editing.id}`, form);
      toast.success("Parent updated");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/coach/parents/${del.id}`); toast.success("Parent deactivated"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  const toggle = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const doInvite = async () => {
    if (!inviteForm.first_name.trim() || !inviteForm.last_name.trim() || !inviteForm.email.trim()) {
      toast.error("First name, last name and email are required"); return;
    }
    if (inviteForm.student_ids.length === 0) { toast.error("Select at least one student to link"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/coach/parent-invites", inviteForm);
      toast.success("Parent invited — share the setup link below");
      setInviteOpen(false);
      setInviteForm({ first_name: "", last_name: "", email: "", student_ids: [] });
      setSetupLink(data.setup_link);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setInviting(false); }
  };
  const copyLink = () => {
    try { navigator.clipboard.writeText(setupLink); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  return (
    <div>
      <SectionHeader subtitle="Approve parent applications and manage linked parent accounts for your team" testId="parents-section-header"
        actions={<Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setInviteOpen(true)} data-testid="parent-invite-btn"><UserPlus className="mr-2 h-4 w-4" /> Invite parent</Button>} />

      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Pending applications{requests.length > 0 ? ` (${requests.length})` : ""}</h3>
      {requests.length === 0 ? (
        <EmptyState title="No pending parent applications" description="New parent signups for your team will appear here." testId="parent-requests-empty" />
      ) : (
        <Card className="mb-8 border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="parent-requests-table">
            <TableHeader><TableRow className="bg-slate-50">{["Name", "Email", "Email match", "Requested", "Actions"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} data-testid={`parent-request-row-${r.id}`}>
                  <TableCell className="font-semibold text-slate-800">{r.first_name} {r.last_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{r.email}</TableCell>
                  <TableCell>
                    {r.email_matched
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`parent-match-${r.id}`}>Matched: {(r.suggested_students || []).map((s) => s.name).join(", ") || "—"}</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid={`parent-nomatch-${r.id}`}>No match — link manually</span>}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(r.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => openApprove(r)} data-testid={`parent-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => setRejectFor(r)} data-testid={`parent-reject-${r.id}`}><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">Approved parents</h3>
      {parents.length === 0 ? (
        <EmptyState title="No parent accounts yet" description="Approved parents will appear here." testId="parents-empty" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="parents-table">
            <TableHeader><TableRow className="bg-slate-50">{["Name", "Email", "Linked students", "Active", "Actions"].map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {parents.map((p) => (
                <TableRow key={p.id} data-testid={`parent-row-${p.id}`} className={p.is_active === false ? "opacity-50" : ""}>
                  <TableCell className="font-semibold text-slate-800">{p.first_name} {p.last_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{p.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1" data-testid={`parent-links-${p.id}`}>
                      {(p.linked_students || []).length === 0 ? <span className="text-xs text-slate-400">—</span>
                        : p.linked_students.map((s) => <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700"><Link2 className="h-3 w-3" />{s.name}</span>)}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={p.is_active === false ? "inactive" : "active"} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`parent-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDel(p)} data-testid={`parent-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Approve dialog with student link override */}
      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="parent-approve-dialog">
          <DialogHeader><DialogTitle className="font-heading">Approve parent</DialogTitle></DialogHeader>
          {approveFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{approveFor.first_name} {approveFor.last_name} · {approveFor.email}</p>
              {!approveFor.email_matched && (
                <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-700" data-testid="parent-approve-nomatch-hint">
                  This email didn't match any student's emergency contact. Manually select the student(s) to link this parent to.
                </p>
              )}
              <div className="space-y-2">
                <Label className="text-xs">Link to student(s)</Label>
                <StudentPicker roster={roster} selected={approveSel} onToggle={(id) => setApproveSel((s) => toggle(s, id))} testPrefix="approve" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doApprove} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="parent-approve-confirm"><Check className="mr-1 h-4 w-4" /> Approve &amp; link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="parent-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit parent</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="parent-edit-firstname" /></div>
              <div className="space-y-2"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="parent-edit-lastname" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="parent-edit-email" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="parent-edit-active" /><Label>Active (can log in)</Label></div>
            <div className="space-y-2">
              <Label>Linked students</Label>
              <StudentPicker roster={roster} selected={form.linked_student_ids} onToggle={(id) => setForm((f) => ({ ...f, linked_student_ids: toggle(f.linked_student_ids, id) }))} testPrefix="edit" />
            </div>
          </div>
          <DialogFooter><Button onClick={doEdit} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="parent-edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}
        title="Reject parent request?"
        description={rejectFor ? `${rejectFor.first_name} ${rejectFor.last_name} will not be able to log in.` : ""}
        confirmText="Reject" destructive onConfirm={doReject} testId="parent-reject-dialog"
      />
      <ConfirmDialog
        open={!!del} onOpenChange={(o) => !o && setDel(null)}
        title="Deactivate parent?"
        description={del ? `${del.first_name} ${del.last_name} (${del.email}) will be deactivated and can no longer log in.` : ""}
        confirmText="Deactivate" destructive onConfirm={doDelete} testId="parent-deactivate-dialog"
      />

      {/* Invite parent dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="parent-invite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Invite a parent</DialogTitle></DialogHeader>
          <p className="-mt-1 text-sm text-slate-500">Great for a second guardian who needs their own login. We'll create their account and email them a link to set a password.</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>First name</Label><Input value={inviteForm.first_name} onChange={(e) => setInviteForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="parent-invite-firstname" /></div>
              <div className="space-y-2"><Label>Last name</Label><Input value={inviteForm.last_name} onChange={(e) => setInviteForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="parent-invite-lastname" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} placeholder="parent@example.com" data-testid="parent-invite-email" /></div>
            <div className="space-y-2">
              <Label>Link to student(s)</Label>
              <StudentPicker roster={roster} selected={inviteForm.student_ids} onToggle={(id) => setInviteForm((f) => ({ ...f, student_ids: toggle(f.student_ids, id) }))} testPrefix="invite" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doInvite} disabled={inviting} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="parent-invite-send">
              {inviting ? <span className="mr-1 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <UserPlus className="mr-1 h-4 w-4" />} Send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup-link result dialog */}
      <Dialog open={!!setupLink} onOpenChange={(o) => !o && setSetupLink(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="parent-invite-link-dialog">
          <DialogHeader><DialogTitle className="font-heading">Parent invited</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">We emailed them a link to set their password. If it doesn't arrive, share this link directly — it expires in 7 days.</p>
          <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-2">
            <code className="min-w-0 flex-1 truncate text-xs text-slate-700" data-testid="parent-invite-link-value">{setupLink}</code>
            <Button size="sm" variant="outline" className="shrink-0 rounded-full" onClick={copyLink} data-testid="parent-invite-copy"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
          </div>
          <DialogFooter><Button onClick={() => setSetupLink(null)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="parent-invite-link-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
